import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import InsCommentListPage from "@saleor/inscomment/components/InsCommentListPage";
import { useUpdateCommentVisible } from "@saleor/inscomment/mutations";
import { useInsCommentList } from "@saleor/inscomment/queries";
import {
  commentListUrl,
  InsCommentListUrlDialog,
  InsCommentListUrlQueryParams,
  insCommentUrl
} from "@saleor/inscomment/urls";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { useIntl } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filters";
import { getSortQueryVariables } from "./sort";

interface InsCommentListProps {
  params: InsCommentListUrlQueryParams;
}

const InsCommentList: React.FC<InsCommentListProps> = ({ params }) => {
  const paginate = usePaginator();
  const tabs = getFilterTabs();
  const navigate = useNavigator();
  const { updateListSettings, settings } = useListSettings(
    ListViews.INSCOMMENTS_LIST
  );
  const notify = useNotifier();
  const paginationState = createPaginationState(settings?.rowNumber, params);
  const queryVariables = {
    ...paginationState,
    filter: getFilterVariables(params),
    sort: getSortQueryVariables(params)
  };
  const { data, loading, refetch } = useInsCommentList({
    variables: queryVariables
  });
  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    createUrl: commentListUrl,
    getFilterQueryParam,
    navigate,
    params
  });
  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const { isSelected, listElements, reset, toggle, toggleAll } = useBulkActions(
    params.ids
  );

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      commentListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };
  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.comments.pageInfo),
    paginationState,
    params
  );
  const handleSort = createSortHandler(navigate, commentListUrl, params);
  const shop = useShop();
  const intl = useIntl();

  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  const [openModal, closeModal] = createDialogActionHandlers<
    InsCommentListUrlDialog,
    InsCommentListUrlQueryParams
  >(navigate, commentListUrl, params);
  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(commentListUrl());
  };

  const [updateVisible] = useUpdateCommentVisible({
    onCompleted: data => {
      if (data.updateCommentVisible.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
      }
    }
  });
  return (
    <>
      <InsCommentListPage
        comments={data?.comments.edges.map(edge => edge.node)}
        toatlCount={maybe(() => data.comments.totalCount, 0)}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        currentTab={currentTab}
        onAdd={null}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        disabled={loading}
        settings={settings}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onSort={handleSort}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        sort={getSortParams(params)}
        onRowClick={id => () => navigate(insCommentUrl(id))}
        // eslint-disable-next-line no-console
        // onRowClick={id => () => console.log(id)}
        toolbar={<></>}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        currencySymbol={currencySymbol}
        filterOpts={getFilterOpts(params)}
        onFilterChange={changeFilters}
        onVisibleUpdate={id => updateVisible({ variables: { id } })}
      />
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default InsCommentList;
